import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { adDetectionApi } from '../../api'
import { useAuth } from '../../AuthContext'
import { enumLabel, useT } from '../../i18n'
import type { AdDetectionConfig, BannerScanProgress, ScanLogEntry } from '../../types'

/**
 * État des scans publicitaires, partagé par tous les écrans « Détection pubs ».
 *
 * Il vit au-dessus du routage : un scan lancé depuis « Campagnes de scan »
 * continue d'être suivi quand on ouvre « Annonceurs » ou un autre module.
 */
interface AdsState {
  config: AdDetectionConfig | null
  /** Pays du périmètre de l'utilisateur, parmi ceux que le module sait scanner. */
  countries: Array<{ code: string; label: string }>
  running: boolean
  /**
   * Lequel des deux passages tourne encore.
   *
   * Un « Lancer le scan » en déclenche deux, de durées sans commune mesure : la
   * collecte des annonceurs dure quelques minutes, le passage sur les sites
   * surveillés peut durer une heure. Les confondre sous un seul « scan en
   * cours » faisait lire un état bloqué, l'historique affichant « Terminé »
   * pour le premier pendant que le second courait encore.
   */
  runningParts: { ads: boolean; banners: boolean }
  runningCountry: string | null
  /** Un arrêt a été demandé et le site en cours se termine. */
  aborting: boolean
  progress: BannerScanProgress | null
  result: { message: string; isError: boolean } | null
  logs: ScanLogEntry[]
  startScan: (country: string, maxInternalPages: number) => Promise<void>
  abort: () => Promise<void>
  clearResult: () => void
  /** Incrémenté quand des données ont pu changer (scan en cours ou terminé). */
  refreshKey: number
}

const AdsContext = createContext<AdsState | null>(null)

/**
 * Toujours monté (pour ne pas remonter l'application à la première visite),
 * mais inerte tant que l'utilisateur n'a pas ouvert le module : aucune requête.
 */
export function AdsProvider({ enabled, children }: { enabled: boolean; children: React.ReactNode }) {
  const { effectiveCountries } = useAuth()
  const t = useT()
  const [config, setConfig] = useState<AdDetectionConfig | null>(null)
  const [running, setRunning] = useState(false)
  const [runningParts, setRunningParts] = useState({ ads: false, banners: false })
  const [runningCountry, setRunningCountry] = useState<string | null>(null)
  const [aborting, setAborting] = useState(false)
  const [progress, setProgress] = useState<BannerScanProgress | null>(null)
  const [result, setResult] = useState<{ message: string; isError: boolean } | null>(null)
  const [logs, setLogs] = useState<ScanLogEntry[]>([])
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    if (!enabled) return
    adDetectionApi.getConfig().then(setConfig).catch(() => setConfig(null))
  }, [enabled])

  // Le serveur envoie ses propres libellés de pays, en français : on les
  // remplace par ceux du catalogue pour que la liste suive la langue.
  const countries = useMemo(() => {
    const all = (config?.supportedCountries ?? []).map((c) => ({
      code: c.code,
      label: enumLabel(t, 'country', c.code, c.label),
    }))
    return effectiveCountries === null ? all : all.filter((c) => effectiveCountries.includes(c.code))
  }, [config, effectiveCountries, t])

  const syncStatus = useCallback(async () => {
    try {
      const [ads, banners] = await Promise.all([adDetectionApi.getScanStatus(), adDetectionApi.getBannerScanStatus()])
      const isRunning = ads.running || banners.running
      setRunning(isRunning)
      setRunningParts({ ads: ads.running, banners: banners.running })
      if (isRunning) setRunningCountry((prev) => prev ?? ads.countries[0] ?? banners.countries[0] ?? null)
      // Le site en cours n'existe que pendant le passage sur les sites : on ne
      // va le chercher qu'alors, et un échec garde le dernier site connu
      // plutôt que de faire clignoter le bandeau.
      if (banners.running) {
        try {
          setProgress((await adDetectionApi.getBannerProgress()).progress)
        } catch {
          /* on garde l'affichage précédent */
        }
      } else {
        setProgress(null)
      }
      return isRunning
    } catch {
      return false
    }
  }, [])

  useEffect(() => {
    if (enabled) void syncStatus()
  }, [enabled, syncStatus])

  // Tant qu'un scan tourne : suivi du site visité et rafraîchissement des écrans.
  useEffect(() => {
    if (!running) return
    const timer = setInterval(async () => {
      const still = await syncStatus()
      setRefreshKey((k) => k + 1)
      if (!still) {
        setProgress(null)
        setRunningCountry(null)
        setAborting(false)
      }
    }, 5000)
    return () => clearInterval(timer)
  }, [running, syncStatus])

  const startScan = useCallback(
    async (country: string, maxInternalPages: number) => {
      if (running || !country) return
      setRunning(true)
      setRunningParts({ ads: true, banners: true })
      setAborting(false)
      setRunningCountry(country)
      setResult(null)
      setLogs([])
      setProgress(null)
      const label = enumLabel(t, 'country', country, country)
      try {
        const [ads, banners] = await Promise.allSettled([
          adDetectionApi.scanCountry(country),
          adDetectionApi.scanBanners(country, maxInternalPages || undefined),
        ])
        const parts: string[] = []
        parts.push(
          ads.status === 'fulfilled'
            ? t('ads.scanPartAds', {
                total: ads.value.total,
                google: ads.value.google,
                meta: ads.value.meta,
                fresh: ads.value.newAdvertisers,
              })
            : t('ads.scanPartAdsFailed'),
        )
        let aborted = false
        if (banners.status === 'fulfilled') {
          aborted = !!banners.value.aborted
          parts.push(
            t('ads.scanPartBanners', {
              banners: banners.value.totalBannersDetected,
              sites: banners.value.totalSitesScanned,
            }),
          )
          if (banners.value.scanLogs?.length) setLogs(banners.value.scanLogs)
        } else {
          parts.push(t('ads.scanPartBannersFailed'))
        }
        setResult({
          message: t('ads.scanResult', {
            head: aborted ? t('ads.scanInterrupted') : t('ads.scanFinished'),
            country: label,
            parts: parts.join(' · '),
          }),
          isError: ads.status === 'rejected' && banners.status === 'rejected',
        })
      } catch (err) {
        setResult({
          message: t('ads.scanFailed', { error: err instanceof Error ? err.message : t('ads.unknownError') }),
          isError: true,
        })
      } finally {
        setProgress(null)
        setRunningCountry(null)
        await syncStatus()
        setRefreshKey((k) => k + 1)
      }
    },
    [running, config, syncStatus, t],
  )

  /**
   * Demande l'arrêt du passage sur les sites.
   *
   * Le drapeau d'arrêt n'est relu qu'entre deux sites, et un site peut occuper
   * le robot deux ou trois minutes. Sans accusé de réception immédiat, le clic
   * paraissait sans effet : un utilisateur a cliqué deux fois en quatre
   * secondes, le 30/10, alors que sa première demande était bien partie et que
   * le scan s'est arrêté quatorze secondes plus tard.
   */
  const abort = useCallback(async () => {
    // Le pays peut manquer si l'écran a été ouvert après le lancement : on le
    // relit plutôt que de ne rien faire en silence.
    let country = runningCountry
    if (!country) {
      try {
        country = (await adDetectionApi.getBannerScanStatus()).countries[0] ?? null
      } catch {
        country = null
      }
    }
    if (!country) {
      setResult({ message: t('ads.nothingToStop'), isError: true })
      return
    }
    setAborting(true)
    try {
      await adDetectionApi.abortBannerScan(country)
    } catch (err) {
      setAborting(false)
      setResult({
        message: t('ads.abortFailed', { error: err instanceof Error ? err.message : t('ads.unknownError') }),
        isError: true,
      })
    }
  }, [runningCountry, t])

  const value: AdsState = {
    config,
    countries,
    running,
    runningParts,
    runningCountry,
    aborting,
    progress,
    result,
    logs,
    startScan,
    abort,
    clearResult: () => setResult(null),
    refreshKey,
  }

  return <AdsContext.Provider value={value}>{children}</AdsContext.Provider>
}

export function useAds(): AdsState {
  const ctx = useContext(AdsContext)
  if (!ctx) throw new Error('useAds doit être utilisé sous AdsProvider actif')
  return ctx
}
